import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaUsers, FaFilter, FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import styles from './ProjectsModal.module.css';

const ProjectsModal = ({ employee, onClose }) => {
  const { isDarkMode } = useTheme();
  const { userAuthorization } = useUserProfile();
  
  // Check if user is admin
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');
  
  const [projects, setProjects] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [showAssignedOnly, setShowAssignedOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);

  // Close modal when clicking outside
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  }, [onClose]);

  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Helper function to format Firestore timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    // Check if it's a Firestore Timestamp
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString('tr-TR'); // Using Turkish locale
    }
    if (typeof timestamp === 'string') {
      try {
        return new Date(timestamp).toLocaleDateString('tr-TR');
      } catch (e) {
        return timestamp;
      }
    }
    return '-';
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'Projects'));
        const projectsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
        
        // Filter assigned projects - exclude projects where employee is passive in all centers
        const assigned = projectsData.filter(project =>
          employee.projects?.some(p => p.id === project.id && (p.status === 'active' || !p.status))
        );
        setAssignedProjects(assigned);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [employee]);

  const truncateText = (text, limit = 40) => {
    if (!text) return '-';
    return text.length > limit ? `${text.substring(0, limit)}...` : text;
  };

  const isProjectPassive = (projectId) => {
    const employeeProject = employee.projects?.find(p => p.id === projectId);
    return employeeProject?.status === 'passive';
  };

  return (
    <div 
      className={styles.modalOverlay} 
      ref={modalRef}
      onClick={handleOverlayClick}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <h2 className={styles.modalTitle}>
              <FaUsers />
              Projeler - {employee.eName} {employee.eSurname}
            </h2>
          </div>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.tableContainer}>
            <table className={styles.projectsTable}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th>Proje Kodu</th>
                  <th>Protokol Numarası</th>
                  <th>Proje Tarihi</th>
                  <th>Proje Adı</th>
                  {isAdmin && <th>FTE</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className={styles.emptyMessage}>
                      Projeler yükleniyor...
                    </td>
                  </tr>
                ) : (showAssignedOnly ? assignedProjects : projects).length === 0 ? (
                  <tr>
                    <td colSpan="5" className={styles.emptyMessage}>
                      {showAssignedOnly
                        ? "Bu çalışana atanmış proje bulunmamaktadır."
                        : "Hiç proje bulunmamaktadır."}
                    </td>
                  </tr>
                ) : (
                  (showAssignedOnly ? assignedProjects : projects).map(project => {
                    const employeeProject = employee.projects?.find(p => p.id === project.id);
                    const rateType = employeeProject?.rate?.type?.toUpperCase();
                    const fteValue = rateType === 'FTE'
                      ? (employeeProject?.rate?.value || '-')
                      : 'Saatlik';

                    return (
                      <tr
                        key={project.id}
                        className={styles.tableRow}
                      >
                        <td className={`${styles.tableCell} ${styles.projectCode}`}>
                          {project.titleShort || '-'}
                        </td>
                        <td className={`${styles.tableCell} ${styles.protocolNumber}`}>
                          {project.protocolNumber || '-'}
                        </td>
                        <td className={`${styles.tableCell} ${styles.projectDate}`}>
                          {formatDate(project.projectDate)}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.projectName}`}
                          title={project.titleLong}
                        >
                          {truncateText(project.titleLong)}
                        </td>
                        {isAdmin && (
                          <td className={`${styles.tableCell} ${styles.projectFte}`}>
                            {fteValue}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsModal;